import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../components/footer";

const INK = "#026E78";
const BRAND = "#4DB1A1";
const MEO_COLOR = "#0033E6";
const IC_COLOR = "#E83B3C";

const COPY = {
  pt: {
    back: "Voltar",
    heroEyebrow: "Portugal Fintech Report 2025",
    heroHeadline: "Transformar planos de pagamento numa experiência digital centrada no cliente",
    heroSub: "A Invisible Collector colabora com a MEO no desenvolvimento de uma plataforma self-service que permite aos clientes gerir planos de pagamento de forma autónoma, segura e eficiente, reduzindo a carga operacional das equipas de apoio.",
    heroTags: ["Self-service", "Planos de Pagamento", "IA", "Compliance", "UX Personalizada", "Co-criação"],

    challengeEyebrow: "O Desafio",
    challengeHeadline: "Um problema real, à escala de um operador nacional",
    challengeText: "A MEO identificou um padrão claro: os seus clientes precisavam de uma forma mais rápida e simples de gerir planos de pagamento, sem precisar de ligar para o call center. O processo existente era lento, gerava volume de chamadas desnecessário e colocava pressão sobre as equipas de apoio. A solução teria de ser digital, autónoma e totalmente alinhada com os padrões da MEO.",
    challengeHighlights: [
      { label: "Alto volume de chamadas", desc: "Planos de pagamento eram geridos maioritariamente via call center" },
      { label: "Processo demorado", desc: "Sem autonomia digital, os clientes dependiam de interação humana" },
      { label: "Pressão operacional", desc: "As equipas de suporte estavam sobrecarregadas com pedidos repetitivos" },
    ],

    solutionEyebrow: "A Solução",
    solutionHeadline: "Uma plataforma adaptada ao contexto da MEO",
    solutionText: "O Invisible Collector desenvolveu uma versão dedicada da sua plataforma de cobranças orientada por IA, especificamente ajustada aos fluxos operacionais, requisitos de compliance, padrões de comunicação e identidade visual da MEO. O resultado é uma plataforma self-service que os clientes podem usar a qualquer momento, sem fricção.",
    solutionFeatures: [
      { tag: "Self-service", title: "Maior autonomia", desc: "Os clientes criam e acompanham os seus planos de pagamento de forma autónoma, quando quiserem." },
      { tag: "Personalização", title: "Adaptada à MEO", desc: "Fluxos, design e funcionalidades desenvolvidos especificamente para os processos e identidade da MEO." },
      { tag: "Compliance", title: "Alinhada com requisitos de compliance", desc: "A solução foi desenvolvida considerando transparência, GDPR e processos regulatórios." },
      { tag: "IA", title: "Plataforma orientada por IA", desc: "A solução parte da tecnologia AI-driven da Invisible Collector e foi adaptada aos processos específicos da MEO." },
    ],

    coCreationEyebrow: "Co-criação",
    coCreationHeadline: "Inovação que nasceu da colaboração",
    coCreationText: "Este não foi apenas um projeto de implementação técnica. Foi uma co-criação entre equipas. A MEO trouxe o conhecimento operacional, os desafios reais do atendimento e as necessidades dos clientes. O Invisible Collector trouxe a tecnologia, a experiência em pagamentos digitais e a capacidade de adaptar uma plataforma complexa a um caso concreto de grande escala.",
    coCreationSides: [
      {
        who: "MEO",
        color: MEO_COLOR,
        items: ["Conhecimento operacional profundo", "Necessidades reais dos clientes", "Requisitos de compliance e regulação", "Identidade visual e standards de comunicação"],
      },
      {
        who: "Invisible Collector",
        color: IC_COLOR,
        items: ["Plataforma de cobranças orientada por IA", "Experiência em pagamentos digitais", "Capacidade de personalização e escala", "Integração em contextos regulatórios complexos"],
      },
    ],

    implEyebrow: "Implementação",
    implHeadline: "Validado antes de chegar ao cliente final",
    implText: "A solução foi testada internamente com utilizadores selecionados da MEO antes do lançamento. O foco não esteve apenas na tecnologia: a experiência de utilização, o layout, a comunicação e a consistência com a identidade da MEO foram trabalhados em detalhe. O produto foi sendo ajustado com base em feedback real e dados de comportamento.",
    implSteps: [
      { n: "01", title: "Piloto interno", desc: "Utilizadores selecionados da MEO testaram internamente a plataforma." },
      { n: "02", title: "UX e consistência", desc: "Design, linguagem e fluxos alinhados com a identidade e os standards da MEO." },
      { n: "03", title: "Iteração com feedback real", desc: "A experiência foi ajustada com base no feedback das equipas, no comportamento dos utilizadores e em dados de negócio." },
    ],

    impactEyebrow: "Impacto",
    impactHeadline: "Em fase piloto. Primeiros indicadores promissores.",
    impactText: "Ainda em fase de piloto, mas os primeiros indicadores apontam para um impacto significativo, tanto na experiência do cliente como na eficiência operacional da MEO.",
    impactItems: [
      { icon: "phone", title: "Menos chamadas", desc: "Expectativa de redução significativa do volume de contactos ao call center relacionados com planos de pagamento." },
      { icon: "clock", title: "Processo mais rápido", desc: "Criação de planos em minutos, sem necessidade de interação humana." },
      { icon: "user", title: "Melhor experiência digital", desc: "Os clientes podem gerir planos de pagamento de forma mais simples, autónoma e sem fricção." },
      { icon: "team", title: "Equipas mais alinhadas", desc: "Colaboração interna reforçada em torno de um objetivo comum." },
    ],

    closingEyebrow: "Reconhecimento",
    closingTitle: "Destaque no Portugal Fintech Report 2025",
    closingText: "Esta parceria foi destacada no Portugal Fintech Report 2025 como exemplo de inovação através da colaboração: uma solução construída a partir da combinação entre conhecimento operacional e tecnologia especializada.",
    closingQuote: "Temos orgulho de fazer parte de um projeto que não está apenas a digitalizar pagamentos, mas a redefinir a forma como empresas e clientes interagem em torno de compromissos financeiros.",

    ctaTitle: "Tem um desafio semelhante?",
    ctaDesc: "Fale connosco sobre como podemos adaptar a nossa plataforma ao seu contexto.",
    ctaLabel: "Fale connosco",
  },
  en: {
    back: "Back",
    heroEyebrow: "Portugal Fintech Report 2025",
    heroHeadline: "Turning payment plans into a customer-centred digital experience",
    heroSub: "Invisible Collector partners with MEO to develop a self-service platform that lets customers manage payment plans autonomously, securely and efficiently, reducing the operational burden on support teams.",
    heroTags: ["Self-service", "Payment Plans", "AI", "Compliance", "Custom UX", "Co-creation"],

    challengeEyebrow: "The Challenge",
    challengeHeadline: "A real problem, at the scale of a national operator",
    challengeText: "MEO identified a clear pattern: customers needed a faster, simpler way to manage payment plans, without calling the call center. The existing process was slow, generated unnecessary call volume and put pressure on support teams. The solution had to be digital, autonomous and fully aligned with MEO's standards.",
    challengeHighlights: [
      { label: "High call volume", desc: "Payment plans were mostly managed through the call center" },
      { label: "Slow process", desc: "Without digital autonomy, customers depended on human interaction" },
      { label: "Operational pressure", desc: "Support teams were overwhelmed with repetitive requests" },
    ],

    solutionEyebrow: "The Solution",
    solutionHeadline: "A platform adapted to MEO's context",
    solutionText: "Invisible Collector developed a dedicated version of its AI-driven collections platform, specifically tailored to MEO's operational flows, compliance requirements, communication standards and visual identity. The result is a self-service platform that customers can use at any time, without friction.",
    solutionFeatures: [
      { tag: "Self-service", title: "Greater autonomy", desc: "Customers create and track their payment plans autonomously, whenever they want." },
      { tag: "Customisation", title: "Adapted to MEO", desc: "Flows, design and features developed specifically for MEO's processes and identity." },
      { tag: "Compliance", title: "Aligned with compliance requirements", desc: "The solution was developed considering transparency, GDPR and regulatory processes." },
      { tag: "AI", title: "AI-driven platform", desc: "The solution builds on Invisible Collector's AI-driven technology, adapted to MEO's specific payment processes." },
    ],

    coCreationEyebrow: "Co-creation",
    coCreationHeadline: "Innovation born from collaboration",
    coCreationText: "This was not just a technical implementation project. It was co-creation between teams. MEO brought operational knowledge, real customer challenges and support needs. Invisible Collector brought the technology, digital payments expertise and the ability to adapt a complex platform to a specific large-scale use case.",
    coCreationSides: [
      {
        who: "MEO",
        color: MEO_COLOR,
        items: ["Deep operational knowledge", "Real customer needs", "Compliance and regulatory requirements", "Visual identity and communication standards"],
      },
      {
        who: "Invisible Collector",
        color: IC_COLOR,
        items: ["AI-driven collections platform", "Digital payments expertise", "Customisation and scale capabilities", "Integration in complex regulatory contexts"],
      },
    ],

    implEyebrow: "Implementation",
    implHeadline: "Validated before reaching the end customer",
    implText: "The solution was tested internally with selected MEO users before launch. The focus was not only on technology: user experience, layout, communication and consistency with MEO's identity were worked on in detail. The product was continuously refined based on real feedback and behavioural data.",
    implSteps: [
      { n: "01", title: "Internal pilot", desc: "Selected MEO users tested the platform internally." },
      { n: "02", title: "UX and consistency", desc: "Design, language and flows aligned with MEO's identity and standards." },
      { n: "03", title: "Iteration with real feedback", desc: "The experience was refined based on team feedback, user behaviour and business data." },
    ],

    impactEyebrow: "Impact",
    impactHeadline: "In pilot phase. Early indicators are promising.",
    impactText: "Still in the pilot phase, but early indicators point to significant impact, both in customer experience and MEO's operational efficiency.",
    impactItems: [
      { icon: "phone", title: "Fewer calls", desc: "Expected significant reduction in call center contacts related to payment plans." },
      { icon: "clock", title: "Faster process", desc: "Plans created in minutes, without the need for human interaction." },
      { icon: "user", title: "Better digital experience", desc: "Customers can manage payment plans more simply, autonomously and without friction." },
      { icon: "team", title: "More aligned teams", desc: "Internal collaboration reinforced around a common goal." },
    ],

    closingEyebrow: "Recognition",
    closingTitle: "Featured in the Portugal Fintech Report 2025",
    closingText: "This partnership was featured in the Portugal Fintech Report 2025 as an example of innovation through collaboration: a solution built from the combination of operational knowledge and specialised technology.",
    closingQuote: "We're proud to be part of a project that's not just digitalising payments, but redefining how companies and customers interact around financial commitments.",

    ctaTitle: "Have a similar challenge?",
    ctaDesc: "Talk to us about how we can adapt our platform to your context.",
    ctaLabel: "Talk to us",
  },
  es: {
    back: "Volver",
    heroEyebrow: "Portugal Fintech Report 2025",
    heroHeadline: "Convertir planes de pago en una experiencia digital centrada en el cliente",
    heroSub: "Invisible Collector colabora con MEO en el desarrollo de una plataforma self-service que permite a los clientes gestionar planes de pago de forma autónoma, segura y eficiente, reduciendo la carga operativa de los equipos de soporte.",
    heroTags: ["Self-service", "Planes de Pago", "IA", "Compliance", "UX Personalizada", "Co-creación"],

    challengeEyebrow: "El Desafío",
    challengeHeadline: "Un problema real, a escala de un operador nacional",
    challengeText: "MEO identificó un patrón claro: sus clientes necesitaban una forma más rápida y sencilla de gestionar planes de pago, sin tener que llamar al call center. El proceso existente era lento, generaba volumen de llamadas innecesario y presionaba a los equipos de soporte. La solución debía ser digital, autónoma y totalmente alineada con los estándares de MEO.",
    challengeHighlights: [
      { label: "Alto volumen de llamadas", desc: "Los planes de pago se gestionaban mayoritariamente por call center" },
      { label: "Proceso lento", desc: "Sin autonomía digital, los clientes dependían de interacción humana" },
      { label: "Presión operativa", desc: "Los equipos de soporte estaban sobrecargados con solicitudes repetitivas" },
    ],

    solutionEyebrow: "La Solución",
    solutionHeadline: "Una plataforma adaptada al contexto de MEO",
    solutionText: "Invisible Collector desarrolló una versión dedicada de su plataforma de cobros impulsada por IA, específicamente adaptada a los flujos operativos, requisitos de compliance, estándares de comunicación e identidad visual de MEO. El resultado es una plataforma self-service que los clientes pueden usar en cualquier momento, sin fricción.",
    solutionFeatures: [
      { tag: "Self-service", title: "Mayor autonomía", desc: "Los clientes crean y acompañan sus planes de pago de forma autónoma, cuando quieran." },
      { tag: "Personalización", title: "Adaptada a MEO", desc: "Flujos, diseño y funcionalidades desarrollados específicamente para los procesos e identidad de MEO." },
      { tag: "Compliance", title: "Alineada con requisitos de compliance", desc: "La solución fue desarrollada considerando transparencia, GDPR y procesos regulatorios." },
      { tag: "IA", title: "Plataforma orientada por IA", desc: "La solución parte de la tecnología AI-driven de Invisible Collector y fue adaptada a los procesos específicos de MEO." },
    ],

    coCreationEyebrow: "Co-creación",
    coCreationHeadline: "Innovación nacida de la colaboración",
    coCreationText: "Este no fue solo un proyecto de implementación técnica. Fue una co-creación entre equipos. MEO aportó el conocimiento operativo, los desafíos reales de atención y las necesidades de los clientes. Invisible Collector aportó la tecnología, la experiencia en pagos digitales y la capacidad de adaptar una plataforma compleja a un caso concreto de gran escala.",
    coCreationSides: [
      {
        who: "MEO",
        color: MEO_COLOR,
        items: ["Conocimiento operativo profundo", "Necesidades reales de los clientes", "Requisitos de compliance y regulación", "Identidad visual y estándares de comunicación"],
      },
      {
        who: "Invisible Collector",
        color: IC_COLOR,
        items: ["Plataforma de cobros impulsada por IA", "Experiencia en pagos digitales", "Capacidad de personalización y escala", "Integración en contextos regulatorios complejos"],
      },
    ],

    implEyebrow: "Implementación",
    implHeadline: "Validado antes de llegar al cliente final",
    implText: "La solución fue probada internamente con usuarios seleccionados de MEO antes del lanzamiento. El foco no estuvo solo en la tecnología: la experiencia de uso, el layout, la comunicación y la consistencia con la identidad de MEO se trabajaron en detalle. El producto se fue ajustando con base en feedback real y datos de comportamiento.",
    implSteps: [
      { n: "01", title: "Piloto interno", desc: "Usuarios seleccionados de MEO probaron internamente la plataforma." },
      { n: "02", title: "UX y consistencia", desc: "Diseño, lenguaje y flujos alineados con la identidad y estándares de MEO." },
      { n: "03", title: "Iteración con feedback real", desc: "La experiencia fue ajustada con base en el feedback de los equipos, comportamiento de los usuarios y datos de negocio." },
    ],

    impactEyebrow: "Impacto",
    impactHeadline: "En fase piloto. Primeros indicadores prometedores.",
    impactText: "Aún en fase piloto, pero los primeros indicadores apuntan a un impacto significativo, tanto en la experiencia del cliente como en la eficiencia operativa de MEO.",
    impactItems: [
      { icon: "phone", title: "Menos llamadas", desc: "Expectativa de reducción significativa del volumen de contactos al call center relacionados con planes de pago." },
      { icon: "clock", title: "Proceso más rápido", desc: "Creación de planes en minutos, sin necesidad de interacción humana." },
      { icon: "user", title: "Mejor experiencia digital", desc: "Los clientes pueden gestionar planes de pago de forma más simple, autónoma y sin fricción." },
      { icon: "team", title: "Equipos más alineados", desc: "Colaboración interna reforzada en torno a un objetivo común." },
    ],

    closingEyebrow: "Reconocimiento",
    closingTitle: "Destacado en el Portugal Fintech Report 2025",
    closingText: "Esta alianza fue destacada en el Portugal Fintech Report 2025 como ejemplo de innovación a través de la colaboración: una solución construida a partir de la combinación entre conocimiento operativo y tecnología especializada.",
    closingQuote: "Estamos orgullosos de formar parte de un proyecto que no solo digitaliza pagos, sino que redefine cómo empresas y clientes interactúan en torno a compromisos financieros.",

    ctaTitle: "¿Tiene un desafío similar?",
    ctaDesc: "Hable con nosotros sobre cómo podemos adaptar nuestra plataforma a su contexto.",
    ctaLabel: "Contáctenos",
  },
};

/* ── Icons ── */
const Icon = ({ type }) => {
  const cls = "h-5 w-5 sm:h-6 sm:w-6 transition-transform duration-300 group-hover:scale-110";
  if (type === "phone") return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.55 11.06a19.79 19.79 0 01-3.07-8.67A2 2 0 012.48 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;
  if (type === "clock") return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
  if (type === "user") return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
  return <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>;
};

const CheckIcon = () => (
  <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

/* ── Section heading helper ── */
const SectionHeader = ({ eyebrow, title, sub, light = false }) => (
  <div className="reveal mb-10 sm:mb-12 text-center">
    <p className={`text-[11px] font-bold uppercase tracking-[0.18em] ${light ? "text-white/60" : "text-slate-400"}`}>
      {eyebrow}
    </p>
    <h2 className={`mt-2 text-2xl font-extrabold sm:text-3xl lg:text-[2rem] ${light ? "text-white" : ""}`}
      style={light ? undefined : { color: INK }}>
      {title}
    </h2>
    {sub && (
      <p className={`mx-auto mt-4 max-w-2xl text-sm leading-relaxed sm:text-base ${light ? "text-white/75" : "text-slate-600"}`}>
        {sub}
      </p>
    )}
  </div>
);

export default function Projetos({ lang: propLang = "pt" }) {
  const [searchParams] = useSearchParams();
  const queryLang = searchParams.get("lang");
  const lang = ["pt", "en", "es"].includes(queryLang) ? queryLang : propLang;
  const copy = COPY[lang] || COPY.pt;
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("reveal-visible"); observer.unobserve(e.target); }
      }),
      { threshold: 0.07, rootMargin: "0px 0px -2% 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => {
      if (!el.classList.contains("reveal-visible")) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const homeHref = lang === "en" ? "/en" : "/pt";

  return (
    <div className="proj-page min-h-screen bg-white font-sans overflow-x-hidden">

      {/* ══════════════════════════════════
           HERO
         ══════════════════════════════════ */}
      <section
        className="relative overflow-hidden px-4 pt-14 pb-20 sm:px-6 sm:pt-20 sm:pb-28 lg:px-8 lg:pb-32"
        style={{ background: `linear-gradient(140deg, ${INK} 0%, #0d9488 55%, ${BRAND} 100%)` }}
      >
        {/* Dot texture */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }}
        />
        {/* Glow orb */}
        <div className="pointer-events-none absolute -top-40 right-0 h-[400px] w-[400px] rounded-full opacity-10 sm:h-[600px] sm:w-[600px]"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,0.5), transparent 70%)" }}
        />

        {/* Back button */}
        <button
          type="button"
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate(homeHref)}
          className="absolute left-5 top-6 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 backdrop-blur transition hover:border-[#38c8ba]/40 hover:text-[#38c8ba] sm:left-8 sm:top-8"
          aria-label={copy.back}
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="relative mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">
          {/* Left: text content */}
          <div className="text-center lg:text-left">
            {/* Eyebrow pill */}
            <div className="reveal mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 backdrop-blur sm:mb-6 sm:px-4">
              <span className="h-1.5 w-1.5 rounded-full bg-white/90 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/90 sm:text-[11px]">{copy.heroEyebrow}</span>
            </div>

            {/* Headline */}
            <h1 className="reveal text-2xl font-extrabold leading-tight text-white sm:text-4xl md:text-[2.75rem] lg:text-5xl" data-reveal="fade-up">
              {copy.heroHeadline}
            </h1>

            {/* Sub */}
            <p className="reveal mt-4 max-w-xl text-sm leading-relaxed text-white/75 sm:mt-5 sm:text-base lg:text-lg lg:max-w-2xl" data-reveal="fade-up">
              {copy.heroSub}
            </p>

            {/* Partnership badge */}
            <div className="reveal mt-6 inline-flex flex-wrap items-center justify-center lg:justify-start gap-3 rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 backdrop-blur sm:mt-8 sm:gap-4 sm:px-6 sm:py-3 proj-partner-badge" data-reveal="fade-up">
              <img src="/assets/Invisible-Collector-Logo.png" alt="Invisible Collector" className="h-5 w-auto brightness-0 invert sm:h-6 lg:h-7" />
              <span className="text-white/40 text-lg font-light sm:text-xl">×</span>
              <img src="/assets/meo.png" alt="MEO" className="h-10 w-auto brightness-0 invert sm:h-12 lg:h-14" />
            </div>
          </div>

          {/* Right: source report image */}
          <div className="reveal flex justify-center lg:justify-end" data-reveal="fade-up">
            <a
              href="/assets/projetos/fintech.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-block"
            >
              <div className="relative w-56 sm:w-72 lg:w-80 rounded-xl overflow-hidden border border-white/20 shadow-2xl transition-transform duration-300 group-hover:scale-[1.03] group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.3)] rotate-[-1.5deg] group-hover:rotate-0">
                <img
                  src="/assets/projetos/fintech.jpeg"
                  alt="Portugal Fintech Report 2025"
                  className="w-full h-auto"
                />
              </div>
              <p className="mt-3 text-[10px] text-white/50 tracking-wide uppercase text-center">
                {lang === "es" ? "Fuente" : lang === "en" ? "Source" : "Fonte"}: Portugal Fintech Report 2025
              </p>
            </a>
          </div>
        </div>

        {/* Wave */}
        <div className="pointer-events-none absolute bottom-0 left-0 w-full">
          <svg viewBox="0 0 1440 56" preserveAspectRatio="none" className="block w-full h-8 sm:h-10 lg:h-14">
            <path d="M0,32 C480,72 960,0 1440,32 L1440,56 L0,56 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════
           O DESAFIO
         ══════════════════════════════════ */}
      <section className="bg-white px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <SectionHeader eyebrow={copy.challengeEyebrow} title={copy.challengeHeadline} />

          <div className="grid gap-8 md:grid-cols-2 md:gap-12 lg:gap-16 items-center">
            <p className="reveal text-sm leading-relaxed text-slate-600 sm:text-base lg:text-[1.05rem]" data-reveal="fade-right">
              {copy.challengeText}
            </p>

            <div className="space-y-3 sm:space-y-4">
              {copy.challengeHighlights.map((h, i) => (
                <div
                  key={h.label}
                  className="proj-highlight-card reveal flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 sm:gap-4 sm:p-5"
                  data-reveal="fade-left"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="proj-highlight-dot mt-[0.35rem] h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: BRAND }} />
                  <div>
                    <p className="font-bold text-slate-800 text-sm">{h.label}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500 sm:text-sm">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           A SOLUÇÃO
         ══════════════════════════════════ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24" style={{ backgroundColor: "#F0FAF8" }}>
        <div className="mx-auto max-w-6xl">
          <SectionHeader eyebrow={copy.solutionEyebrow} title={copy.solutionHeadline} sub={copy.solutionText} />

          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {copy.solutionFeatures.map((f, i) => (
              <div
                key={f.tag}
                className="proj-feature-card reveal group rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100 sm:p-6"
                data-reveal="fade-up"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <span
                  className="mb-3 inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white transition-transform duration-300 group-hover:scale-105 sm:mb-4"
                  style={{ backgroundColor: i % 2 === 0 ? BRAND : INK }}
                >
                  {f.tag}
                </span>
                <h3 className="font-extrabold text-sm sm:text-base" style={{ color: INK }}>{f.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500 sm:mt-2 sm:text-sm">{f.desc}</p>
                <div className="proj-feature-bar mt-3 h-0.5 w-0 rounded-full transition-all duration-500 group-hover:w-full sm:mt-4" style={{ backgroundColor: i % 2 === 0 ? BRAND : INK }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           CO-CRIAÇÃO
         ══════════════════════════════════ */}
      <section className="bg-white px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <SectionHeader eyebrow={copy.coCreationEyebrow} title={copy.coCreationHeadline} sub={copy.coCreationText} />

          <div className="grid gap-5 sm:grid-cols-2">
            {copy.coCreationSides.map((side, i) => (
              <div
                key={side.who}
                className="proj-cocreation-card reveal rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6 lg:p-8"
                data-reveal={i === 0 ? "fade-right" : "fade-left"}
              >
                <div
                  className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 sm:mb-5 sm:px-4"
                  style={{ backgroundColor: `${side.color}15` }}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: side.color }} />
                  <span className="text-[11px] font-bold uppercase tracking-widest sm:text-xs" style={{ color: side.color }}>{side.who}</span>
                </div>
                <ul className="space-y-2.5 sm:space-y-3">
                  {side.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-600 sm:text-sm">
                      <span className="mt-0.5 shrink-0" style={{ color: side.color }}><CheckIcon /></span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="proj-cocreation-bar mt-5 h-0.5 w-0 rounded-full transition-all duration-700 sm:mt-6" style={{ backgroundColor: side.color }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           IMPLEMENTAÇÃO
         ══════════════════════════════════ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24" style={{ backgroundColor: "#F0FAF8" }}>
        <div className="mx-auto max-w-3xl">
          <SectionHeader eyebrow={copy.implEyebrow} title={copy.implHeadline} sub={copy.implText} />

          <div className="relative">
            {/* Connector line aligned to center of step number */}
            <div
              className="absolute left-5 top-6 bottom-6 w-px hidden sm:left-6 sm:block"
              style={{ backgroundColor: `${BRAND}35` }}
            />
            <div className="space-y-4 sm:space-y-5">
              {copy.implSteps.map((step, i) => (
                <div
                  key={step.n}
                  className="reveal flex items-start gap-3 sm:gap-5"
                  data-reveal="fade-right"
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div
                    className="proj-step-num relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white font-black text-xs shadow-md sm:h-12 sm:w-12 sm:text-sm"
                    style={{ backgroundColor: INK }}
                  >
                    {step.n}
                  </div>
                  <div className="proj-step-card flex-1 rounded-xl bg-white border border-slate-100 p-4 shadow-sm sm:rounded-2xl sm:p-5 lg:p-6">
                    <h3 className="font-extrabold text-sm sm:text-base" style={{ color: INK }}>{step.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600 sm:mt-1.5 sm:text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           IMPACTO
         ══════════════════════════════════ */}
      <section
        className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24"
        style={{ background: `linear-gradient(140deg, ${INK} 0%, #0d9488 55%, ${BRAND} 100%)` }}
      >
        <div className="mx-auto max-w-6xl">
          <SectionHeader
            eyebrow={copy.impactEyebrow}
            title={copy.impactHeadline}
            sub={copy.impactText}
            light
          />

          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {copy.impactItems.map((item, i) => (
              <div
                key={item.title}
                className="proj-impact-card reveal group rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur sm:p-6"
                data-reveal="fade-up"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-white sm:mb-4 sm:h-12 sm:w-12">
                  <Icon type={item.icon} />
                </div>
                <h3 className="font-extrabold text-sm text-white sm:text-base">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-white/70 sm:mt-2 sm:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           RECONHECIMENTO
         ══════════════════════════════════ */}
      <section className="bg-white px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-4xl">
          <div
            className="proj-closing-card reveal rounded-2xl border border-slate-100 bg-slate-50 p-5 sm:p-8 lg:p-12"
            data-reveal="fade-up"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.closingEyebrow}</p>
            <h3 className="mt-2 text-lg font-extrabold sm:text-xl lg:text-2xl" style={{ color: INK }}>{copy.closingTitle}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:mt-4 sm:text-base">{copy.closingText}</p>
            <div className="proj-quote-block mt-6 border-l-4 pl-4 sm:mt-8 sm:pl-6" style={{ borderColor: BRAND }}>
              <p className="text-base font-semibold italic leading-relaxed text-slate-700 sm:text-lg">"{copy.closingQuote}"</p>
              <p className="mt-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-400 sm:mt-2">Invisible Collector</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
           CTA
         ══════════════════════════════════ */}
      <section className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8" style={{ backgroundColor: "#F0FAF8" }}>
        <div className="mx-auto max-w-2xl text-center reveal" data-reveal="fade-up">
          <h2 className="text-xl font-extrabold sm:text-2xl lg:text-3xl" style={{ color: INK }}>{copy.ctaTitle}</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500 sm:mt-3 sm:max-w-md sm:text-base">{copy.ctaDesc}</p>
          <a
            href="mailto:info@invisiblecloud.pt"
            className="proj-cta-btn mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white shadow-md sm:mt-8 sm:gap-2.5 sm:px-8 sm:py-3.5"
            style={{ backgroundColor: INK }}
          >
            {copy.ctaLabel}
            <svg className="proj-cta-arrow h-3.5 w-3.5 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </section>

      <Footer lang={lang} variant="coaching40" />

      {/* ── Scoped styles ── */}
      <style>{`
        .proj-back-btn {
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .proj-back-btn:hover {
          background-color: rgba(255,255,255,0.2);
          transform: translateX(-2px);
        }
        .proj-partner-badge {
          transition: box-shadow 0.3s ease;
        }
        .proj-partner-badge:hover {
          box-shadow: 0 0 0 3px rgba(255,255,255,0.15);
        }
        .proj-tag {
          transition: background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
        }
        .proj-tag:hover {
          background-color: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.4);
          transform: translateY(-2px);
        }
        .proj-highlight-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease, border-color 0.25s ease;
        }
        .proj-highlight-card:hover {
          transform: translateX(6px);
          background-color: #F0FAF8;
          border-color: rgba(77, 177, 161, 0.35);
          box-shadow: 0 4px 16px -6px rgba(2, 110, 120, 0.12);
        }
        .proj-highlight-card:hover .proj-highlight-dot {
          transform: scale(1.6);
          transition: transform 0.25s ease;
        }
        .proj-feature-card {
          transition: transform 0.3s cubic-bezier(0.25, 0.6, 0.3, 1), box-shadow 0.3s ease;
        }
        .proj-feature-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px -12px rgba(2, 110, 120, 0.15), 0 4px 12px -4px rgba(0,0,0,0.08);
        }
        .proj-cocreation-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .proj-cocreation-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px -10px rgba(2, 110, 120, 0.12);
        }
        .proj-cocreation-card:hover .proj-cocreation-bar {
          width: 100%;
        }
        .proj-step-card {
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .proj-step-card:hover {
          transform: translateX(4px);
          box-shadow: 0 8px 24px -8px rgba(2, 110, 120, 0.14);
          border-color: rgba(77, 177, 161, 0.3);
        }
        .proj-step-num {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .reveal:hover .proj-step-num {
          transform: scale(1.08);
          box-shadow: 0 0 0 4px rgba(2, 110, 120, 0.15);
        }
        .proj-impact-card {
          transition: transform 0.3s cubic-bezier(0.25, 0.6, 0.3, 1), background-color 0.3s ease, box-shadow 0.3s ease;
        }
        .proj-impact-card:hover {
          transform: translateY(-6px);
          background-color: rgba(255,255,255,0.18);
          box-shadow: 0 16px 40px -12px rgba(0,0,0,0.3);
        }
        .proj-closing-card {
          transition: box-shadow 0.3s ease;
        }
        .proj-closing-card:hover {
          box-shadow: 0 20px 48px -16px rgba(2, 110, 120, 0.14);
        }
        .proj-quote-block {
          transition: border-color 0.3s ease;
        }
        .proj-closing-card:hover .proj-quote-block {
          border-color: ${INK};
        }
        .proj-cta-btn {
          position: relative;
          overflow: hidden;
          transition: transform 0.28s ease, box-shadow 0.28s ease;
        }
        .proj-cta-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
          background-size: 200% 100%;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .proj-cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px -12px rgba(2, 110, 120, 0.45);
        }
        .proj-cta-btn:hover::after {
          opacity: 1;
          animation: proj-shimmer 1.4s linear infinite;
        }
        .proj-cta-arrow {
          transition: transform 0.25s ease;
        }
        .proj-cta-btn:hover .proj-cta-arrow {
          transform: translateX(4px);
        }
        @keyframes proj-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}
